/**
 * Upload / processing error catalog — maps server error codes to the §25
 * canonical user-facing messages. §24: a user must NEVER see "error",
 * "timeout", or "failed"; unknown/processing failures fall back to one graceful
 * message. UploadZone renders this by `error.code` and NEVER the raw
 * `error.message` (which may carry internal detail).
 * @module dashboard/api/uploadErrors
 */

import { formatDateDDMonYYYY } from '../../utils/dates.js';

// §24-safe graceful default for any processing failure (analysis/render/DB save/unknown).
export const GRACEFUL_PROCESSING_MESSAGE =
  "We couldn't finish processing this schedule. Please try again in a moment.";

function formatNextAvailable(error) {
  // free_report_limit returns `next_available_at` (ISO) in the response body,
  // surfaced on ApiError.details by the fetch wrapper.
  const iso = error?.details?.next_available_at || error?.details?.nextAvailableAt;
  if (!iso) return 'soon';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return 'soon';
  // A1 (D-ListDateFormat): the one shared DD Mon YYYY formatter.
  return formatDateDDMonYYYY(d);
}

/**
 * Resolve the user-facing message for an upload error.
 * @param {{code?: string, details?: object, message?: string}} error - usually an ApiError
 * @returns {string}
 */
export function uploadErrorMessage(error) {
  const code = (error && error.code) || null;
  switch (code) {
    // §25 #1 — wrong file type.
    case 'bad_file_type':
      return 'Please upload a valid .XER file exported from Primavera P6.';
    // §25 #2 — corrupted / incomplete file.
    case 'parse_failed':
      return 'This file appears to be corrupted or incomplete. Try re-exporting from P6.';
    // §25 #3 — empty schedule.
    case 'empty_schedule':
      return 'No schedulable activities found. Ensure your P6 project has active activities.';
    // §25 #4 — no valid projects.
    case 'no_valid_projects':
      return 'No valid project data detected in this file.';
    // Compare-to-baseline (Sub-2a): the second .xer. Mirror the current-file
    // states but point the user at the baseline slot. §24-safe, no internal leak.
    case 'baseline_bad_file_type':
      return 'The baseline must be a valid .XER file exported from Primavera P6.';
    case 'baseline_parse_failed':
      return 'The baseline file appears to be corrupted or incomplete. Try re-exporting from P6.';
    case 'baseline_no_projects':
      return 'No valid project data was found in the baseline file.';
    case 'baseline_empty':
      return 'The baseline file contains no schedulable activities.';
    // §25 #5 — free-tier upload blocked. Reuse the server's config-derived
    // sentence verbatim (e.g. "Free tier allows 5 reports every 30 days. Upgrade
    // for unlimited.") so the cap numbers can NEVER drift from the middleware,
    // then splice in the next-available date. No hardcoded "one report"/"30 days".
    case 'free_report_limit': {
      const serverMsg = typeof error?.details?.message === 'string' ? error.details.message.trim() : '';
      const base = serverMsg || "You've reached your free report limit. Upgrade for unlimited.";
      const when = formatNextAvailable(error);
      if (when === 'soon') return base;
      const cta = /\s*Upgrade for unlimited\.?\s*$/i;
      return cta.test(base)
        ? base.replace(cta, ` Next available: ${when}. Upgrade for unlimited.`)
        : `${base} Next available: ${when}.`;
    }
    // Clean, non-canonical messages (no banned words).
    case 'file_too_large':
      return (typeof error?.message === 'string' && error.message)
        || 'This file is larger than the upload limit. Please re-export, or contact us for very large schedules.';
    // RENDER-SCALE §25 over-capacity — too many activities for a single report.
    // The server message carries the live activity count; reuse it verbatim.
    case 'over_capacity':
      return (typeof error?.message === 'string' && error.message)
        || 'This schedule is larger than CritWise can process in a single report. Split it by sub-project, or contact us for very large schedules.';
    case 'bad_mode':
      return 'Please choose a valid analysis mode and try again.';
    case 'bad_thresholds':
      return 'Those analysis settings were not valid. Please try again.';
    case 'no_file':
      return 'Please choose a .XER file to upload.';
    // S4-2 upload rate limit — back-off guidance, not the generic retry-now copy.
    case 'rate_limited':
      return 'Upload limit reached — please try again in about an hour.';
    // v3.20 §2 — bounded upload queue states. §24-safe (no banned words);
    // mirrors the server envelope copy, rendered from THIS catalogue by code.
    case 'queue_full':
      return "We're processing a high volume of schedules at the moment. Please try again in a few minutes.";
    case 'queue_draining':
      return 'The service is briefly unavailable while it restarts. Please try again in a moment.';
    // analysis_failed / pdf_failed / report_save_failed / processing_failed /
    // unknown → one graceful §24 message.
    default:
      return GRACEFUL_PROCESSING_MESSAGE;
  }
}
